import { Loader } from '@styled-icons/boxicons-regular'
import { MapAlt } from '@styled-icons/boxicons-solid'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'

import { DEFAULT_GEOLOCATION_ZOOM } from '../../constants/map'
import {
  GeolocationState,
  requestGeolocation,
} from '../../redux/geolocationSlice'
import { viewToString } from '../../utils/appUrl'
import { useAppHistory } from '../../utils/useAppHistory'
import AboutSection from '../mobile/AboutSection'
import MobileAppLinks from '../mobile/MobileAppLinks'
import MobileSocialLinks from '../mobile/MobileSocialLinks'
import Button from '../ui/Button'
import { PageTitle } from '../ui/Headers'
import { LandingPage } from '../ui/PageTemplate'
import { withAuthRedirect } from './withAuthRedirect'

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1em;
  margin: 1em 0;
`
const LargeButton = styled(Button)`
  min-height: 2em;
  font-size: 1em;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  font-size: 1em !important;
`
const StyledLinkWhite = styled(StyledLink)`
  color: ${({ theme }) => theme.background} !important;
`

const Description = styled.div`
  text-align: justify;
  color: ${({ theme }) => theme.secondaryText};

  padding: 0.5em;
`

const SpinnerIcon = styled(Loader)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const HomePage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useAppHistory()
  const location = useLocation()
  const bottomRef = useRef(null)
  const { lastMapView } = useSelector((state) => state.viewport)
  const { geolocation, geolocationState } = useSelector(
    (state) => state.geolocation,
  )

  const handleExploreMap = () => {
    if (
      lastMapView ||
      geolocationState === GeolocationState.DENIED ||
      geolocationState === GeolocationState.FAILED
    ) {
      history.push('/map')
    } else {
      dispatch(requestGeolocation())
    }
  }

  useEffect(() => {
    if (lastMapView) {
      return
    }
    if (
      geolocationState === GeolocationState.DENIED ||
      geolocationState === GeolocationState.FAILED
    ) {
      history.push('/map')
    } else if (geolocation?.latitude && geolocation?.longitude) {
      history.push(
        `/map/${viewToString(geolocation.latitude, geolocation.longitude, DEFAULT_GEOLOCATION_ZOOM)}`,
      )
    }
  }, [lastMapView, geolocation, geolocationState, history])

  useEffect(() => {
    if (location.state?.scrollToBottom) {
      bottomRef.current?.scrollIntoView()
    }
  }, [location.state])

  return (
    <LandingPage>
      <PageTitle>{t('pages.welcome.home_page')}</PageTitle>
      <br />
      <Description>
        {t('pages.welcome.welcome_visitors_to_the_site_short')}
        <br />
        <br />
        {t('pages.welcome.community_driven_platform')}
        <br />
        <br />
        {t('pages.welcome.discover_and_contribute')}
      </Description>
      <ButtonWrapper>
        <LargeButton
          type="button"
          leftIcon={
            geolocationState === GeolocationState.LOADING ? (
              <SpinnerIcon height={30} />
            ) : (
              <MapAlt height={30} />
            )
          }
          onClick={handleExploreMap}
          disabled={geolocationState === GeolocationState.LOADING}
        >
          {t('pages.welcome.explore_the_map')}
        </LargeButton>
      </ButtonWrapper>
      <ButtonWrapper style={{ marginTop: '2em' }}>
        <Button type="button" secondary>
          <StyledLink to="/auth/sign_in">{t('users.sign_in')}</StyledLink>
        </Button>
        <Button type="button">
          <StyledLinkWhite to="/auth/sign_up">
            {t('glossary.sign_up')}
          </StyledLinkWhite>
        </Button>
      </ButtonWrapper>
      <MobileAppLinks />
      <AboutSection />
      <br />
      <MobileSocialLinks />
      <div ref={bottomRef} />
    </LandingPage>
  )
}

export default withAuthRedirect(HomePage, false)
